<template>
  <content-dialog
    v-model="dialogOpen"
    fullscreen-mobile
    width="600px"
    title="Send Confirmation"
    persistent
    @hide="reset"
  >
    <q-list>
      <sticky-header>
        Recipients
      </sticky-header>

      <page-banner
        v-if="!hasAnyRecipient"
        color="red-7"
        dark
        dense
        class="q-mx-sm"
        icon="mdi-exclamation"
        label="Select at least one recipient to send this confirmation to"
      />

      <q-list separator>
        <template
          v-for="(clientData, index) in clientsData"
          :key="clientData.client.id"
        >
          <client-item
            :client="clientData.client"
            dense
            without-email
            without-address
          />

          <!-- Email row -->
          <q-item tag="label">
            <q-item-section
              side
              top
              style="min-width: 80px"
            >
              <q-checkbox
                v-model="clientsData[index].sendEmail"
                label="Email"
              />
            </q-item-section>

            <q-item-section>
              <q-input
                v-model="clientsData[index].email"
                :disable="!clientsData[index].sendEmail"
                dense
                outlined
                placeholder="Email address"
                type="email"
                :hint="clientsData[index].emailPrefilled ? 'Prefilled from client profile' : ''"
                hide-bottom-space
              />
            </q-item-section>
          </q-item>

          <!-- SMS row -->
          <q-item tag="label">
            <q-item-section
              side
              top
              style="min-width: 80px"
            >
              <q-checkbox
                v-model="clientsData[index].sendSms"
                label="SMS"
              />
            </q-item-section>

            <q-item-section>
              <q-input
                v-model="clientsData[index].phone"
                :disable="!clientsData[index].sendSms"
                dense
                outlined
                placeholder="Phone number"
                type="tel"
                :hint="clientsData[index].phonePrefilled ? 'Prefilled from client profile' : ''"
                hide-bottom-space
              />
            </q-item-section>
          </q-item>
        </template>
      </q-list>

      <template v-if="willSendSMS">
        <q-card-section>
          <SmsChargeWarning />
        </q-card-section>

        <SandboxSmsNotice
          v-if="isDevelopment"
          class="q-mx-sm"
        />
      </template>
    </q-list>

    <template #bottom>
      <q-toolbar>
        <q-space />
        <q-btn
          v-close-popup
          flat
          color="blue-grey"
          label="Cancel"
        />
        <q-btn
          label="Send Confirmation"
          color="green"
          :disable="!hasAnyRecipient"
          @click="sendConfirmation"
        />
      </q-toolbar>
    </template>
  </content-dialog>
</template>

<script>
/*
  Send a confirmation notification to appointment clients
 */
import SandboxSmsNotice from 'components/partials/sandbox/SandboxSmsNotice'
import SmsChargeWarning from 'components/partials/communications/SmsChargeWarning'
import fillClientDetailsMixin from './mixins/fillClientDetailsMixin'

export default {
  components: {
    SandboxSmsNotice, SmsChargeWarning
  },
  mixins: [fillClientDetailsMixin],
  props: {
    id: {
      type: Number,
      required: true
    },
    clients: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  emits: ['sent'],
  data () {
    return {
      isDevelopment: !!process.env.SANDBOX,
      dialogOpen: false,
      clientsData: []
    }
  },
  computed: {
    hasAnyRecipient () {
      return this.clientsData.some(c =>
        (c.sendEmail && c.email) || (c.sendSms && c.phone)
      )
    },
    willSendSMS () {
      return this.clientsData.some(c => c.sendSms && c.phone)
    }
  },
  methods: {
    open () {
      // Initialize data for each client
      this.clientsData = this.clients.map(client => {
        const defaultEmail = this.baseSelectAppropriateClientDetail('EMAIL', client)
        const defaultPhone = this.baseSelectAppropriateClientDetail('SMS', client)
        const prefersEmail = client.default_communication_method !== 'SMS'
        const prefersSms = client.default_communication_method !== 'EMAIL'

        return {
          client,
          sendEmail: prefersEmail && !!defaultEmail,
          sendSms: prefersSms && !!defaultPhone,
          email: defaultEmail || '',
          phone: defaultPhone || '',
          emailPrefilled: !!defaultEmail,
          phonePrefilled: !!defaultPhone
        }
      })
      this.dialogOpen = true
    },
    reset () {
      this.clientsData = []
    },
    buildReminders () {
      const reminders = []
      for (const data of this.clientsData) {
        if (data.sendEmail && data.email) {
          reminders.push({
            client_id: data.client.id,
            type: 'EMAIL',
            recipient: data.email
          })
        }
        if (data.sendSms && data.phone) {
          reminders.push({
            client_id: data.client.id,
            type: 'SMS',
            recipient: data.phone
          })
        }
      }
      return reminders
    },
    async sendConfirmation () {
      this.$loading.show({ message: 'Sending confirmation...' })
      const reminders = this.buildReminders()
      const { status } = await this.$post('v1/appointments/send-confirmation-email', {
        id: this.id,
        reminders
      })
      this.$loading.hide()
      if (status === 'success') {
        const emailCount = reminders.filter(r => r.type === 'EMAIL').length
        const smsCount = reminders.filter(r => r.type === 'SMS').length
        const parts = []
        if (emailCount) parts.push(`${emailCount} email${emailCount > 1 ? 's' : ''}`)
        if (smsCount) parts.push(`${smsCount} SMS`)
        this.$q.notify({
          color: 'green',
          message: `Confirmation sent: ${parts.join(' and ')}`
        })
        this.$emit('sent')
        this.dialogOpen = false
      }
    }
  }
}
</script>
